import { useState, useEffect } from "react";
import { DateTime } from "luxon";
import { Line } from "react-chartjs-2";
import type { ChartData, ChartOptions } from "chart.js";
import type { TrendSeries } from "@/hooks/useTrendData";
import type { Granularity } from "@/lib/periods";
import type { FilterState, CapacityMetric } from "@/types/filters";
import { getCSSVar } from "@/lib/chart";
import { niceYBounds } from "@/lib/chartScale";
import { Card } from "@/components/ui/card";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";

const TOGGLE_ITEM_CLS =
  "h-auto text-[12px] px-3 py-1 border-black/15 bg-white text-gray-600 font-normal hover:bg-gray-50 hover:text-gray-600 transition-colors data-[state=on]:bg-[var(--brand-dark-teal)] data-[state=on]:text-white data-[state=on]:border-[var(--brand-dark-teal)] data-[state=on]:z-10 disabled:opacity-40 disabled:cursor-not-allowed";

const METRIC_GROUPS: Array<{ key: CapacityMetric; label: string }[]> = [
  [
    { key: "hours", label: "Hours" },
    { key: "days", label: "Days" },
  ],
  [
    { key: "total_hours", label: "Hours" },
    { key: "dvm_day", label: "DVMs/day" },
  ],
];

const METRIC_SUFFIX: Record<CapacityMetric, string> = {
  hours: "h",
  days: "d",
  total_hours: "h",
  dvm_day: "",
};

// Days worked per DVM per day is always 1.0 -- a flat line at every point. Hide
// the option rather than plot a tautology.
const HIDDEN_AT_DAY = new Set<CapacityMetric>(["days"]);

const GRANULARITY_LABEL: Record<string, string> = {
  day: "Daily",
  week: "Weekly",
  month: "Monthly",
};

// Week and Month both read the per-week figure. Normalising to a weekly rate
// rather than a per-bucket total is what keeps the Y-axis stable when switching
// Week <-> Month, and it is the only reading that survives a partial bucket: a
// calendar period's edge month holds 19 days, and a "per month" number there
// would show a cliff that is an artefact of the range, not the schedule.
function extractMetricValues(
  points: TrendSeries["current"],
  metric: CapacityMetric,
  granularity: Granularity
): number[] {
  const perDay = granularity === "day";
  return points.map((p) => {
    switch (metric) {
      case "hours":
        return +(perDay ? p.avgHoursPerDvmPerDay : p.avgHoursPerDvmPerWeek).toFixed(1);
      case "days":
        return +p.daysPerDvmPerWeek.toFixed(1);
      case "total_hours":
        return +p.totalHoursPerWeek.toFixed(1);
      case "dvm_day":
        return +p.dvmsPerDay.toFixed(1);
    }
  });
}

interface CapacityChartProps {
  trend: TrendSeries;
  granularity: FilterState["capGranularity"];
  // Groupings the selected period actually spans — a chart with one point
  // is not a trend, so the rest are disabled rather than hidden.
  availableGranularities: Granularity[];
  // True when the period is calendar-based and grouped by week, so the chart
  // is showing fewer days than the KPI cards above it.
  partialWeeksShown: boolean;
  /** Calendar period grouped by month: the edge bucket is a part-month. */
  partialMonthEdge: boolean;
  capacityMetric: FilterState["capacityMetric"];
  setCapacityMetric: (m: FilterState["capacityMetric"]) => void;
  setCapGranularity: (g: FilterState["capGranularity"]) => void;
}

export function CapacityChart({
  trend,
  granularity,
  availableGranularities,
  partialWeeksShown,
  partialMonthEdge,
  capacityMetric,
  setCapacityMetric,
  setCapGranularity,
}: CapacityChartProps) {
  const [lineColor] = useState(() => getCSSVar("--brand-dark-teal") || "#044f59");

  // A metric hidden at the current granularity would leave the toggle group with
  // no selection, so fall back to the first option in its own row.
  useEffect(() => {
    if (granularity === "day" && HIDDEN_AT_DAY.has(capacityMetric)) {
      setCapacityMetric("hours");
    }
  }, [granularity]); // eslint-disable-line react-hooks/exhaustive-deps

  // Day and week labels carry two parts ("7/27 Mon", "W31 7/27") — split onto
  // two lines so Chart.js keeps them horizontal at wider ranges instead of
  // rotating or dropping most of them. Month labels are a single part.
  const labels =
    granularity === "month"
      ? trend.current.map((p) => p.label)
      : trend.current.map((p) => p.label.split(" "));
  const suffix = METRIC_SUFFIX[capacityMetric];
  const values = extractMetricValues(trend.current, capacityMetric, granularity);
  const { min, max } = niceYBounds(values);

  const data: ChartData<"line"> = {
    labels,
    datasets: [
      {
        label: capacityMetric,
        data: values,
        borderColor: lineColor,
        backgroundColor: `${lineColor}10`,
        fill: true,
        tension: 0.35,
        pointRadius: 3,
        pointBackgroundColor: lineColor,
        borderWidth: 2,
      },
    ],
  };

  const options: ChartOptions<"line"> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          title: (items) => {
            const point = trend.current[items[0]?.dataIndex ?? -1];
            if (!point) return items[0]?.label ?? "";
            return granularity === "day"
              ? DateTime.fromISO(point.bucketKey).toFormat("ccc M/d")
              : point.label;
          },
          label: (ctx) => `${ctx.parsed.y}${suffix}`,
        },
      },
    },
    scales: {
      y: {
        min,
        max,
        ticks: {
          font: { size: 11 },
          color: "#6b6b68",
          callback: (v) => `${v}${suffix}`,
        },
        grid: { color: "#e5e4e0" },
      },
      x: {
        // Never rotate — drop labels instead so they stay horizontal at any
        // range width. Chart.js's default is to rotate before it skips.
        ticks: { font: { size: 11 }, color: "#6b6b68", maxRotation: 0, minRotation: 0 },
        grid: { display: false },
      },
    },
  };

  return (
    <Card className="px-5 py-4 gap-0 shadow-none border-black/10">
      {/* Title left, metric toggles top-right -- matching RevenueMetricChart,
          the only other chart here with an in-card metric switch.

          flex-wrap is load-bearing, not decorative: the title plus both toggle
          groups need ~450px, and this card is ~313px wide at a 1024 viewport.
          Without wrapping they would overflow the card exactly as the Revenue
          trend charts did. Wrapped, they fall back to sitting under the title,
          which is where they used to be. */}
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 mb-3">
        <div className="text-[16px] font-medium">Capacity trend</div>
        <div className="flex items-center gap-4 text-[11px]">
          <div className="flex items-center gap-1.5">
            <span className="text-gray-400 font-medium">Per DVM</span>
            <ToggleGroup
              type="single"
              variant="outline"
              value={capacityMetric}
              onValueChange={(v) => {
                if (v) setCapacityMetric(v as FilterState["capacityMetric"]);
              }}
            >
              {METRIC_GROUPS[0]!
                .filter((m) => !(granularity === "day" && HIDDEN_AT_DAY.has(m.key)))
                .map((m) => (
                  <ToggleGroupItem key={m.key} value={m.key} className={TOGGLE_ITEM_CLS}>
                    {m.label}
                  </ToggleGroupItem>
                ))}
            </ToggleGroup>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="text-gray-400 font-medium">Total</span>
            <ToggleGroup
              type="single"
              variant="outline"
              value={capacityMetric}
              onValueChange={(v) => {
                if (v) setCapacityMetric(v as FilterState["capacityMetric"]);
              }}
            >
              {METRIC_GROUPS[1]!.map((m) => (
                <ToggleGroupItem key={m.key} value={m.key} className={TOGGLE_ITEM_CLS}>
                  {m.label}
                </ToggleGroupItem>
              ))}
            </ToggleGroup>
          </div>
        </div>
      </div>

      <div className="text-[12px] text-gray-500 mb-3">{GRANULARITY_LABEL[granularity]}</div>

      <div className="flex-1 min-h-0 relative">
        <Line data={data} options={options} />
      </div>

      <div className="flex items-center gap-2 mt-3 justify-end">
        <span className="text-[11px] text-gray-500 font-medium">Group by</span>
        <ToggleGroup
          type="single"
          variant="outline"
          value={granularity}
          onValueChange={(v) => {
            if (v) setCapGranularity(v as FilterState["capGranularity"]);
          }}
        >
          {(["day", "week", "month"] as const).map((g) => (
            <ToggleGroupItem
              key={g}
              value={g}
              disabled={!availableGranularities.includes(g)}
              className={TOGGLE_ITEM_CLS}
            >
              {g.charAt(0).toUpperCase() + g.slice(1)}
            </ToggleGroupItem>
          ))}
        </ToggleGroup>
      </div>
      {partialWeeksShown && (
        <p className="text-[11px] text-gray-400 mt-2">
          The first and last points are part-weeks — a calendar period does not start or end on a
          Monday. Their totals are smaller for that reason; the per-DVM rates divide by the days
          actually elapsed, so those stay comparable.
        </p>
      )}
      {partialMonthEdge && (
        <p className="text-[11px] text-gray-400 mt-2">
          Weekly average within each month. The last point covers a partial month, so read it as
          provisional — a short final week counts as a whole one in the per-DVM figures.
        </p>
      )}
    </Card>
  );
}
